import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Key, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const emptyForm = {
  current_password: "",
  new_password: "",
  confirm_password: "",
};

export default function ChangePasswordModal({ open, onClose }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = () => {
    setForm(emptyForm);
    onClose();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("Las contraseñas nuevas no coinciden");
      return;
    }
    if (form.new_password === form.current_password) {
      toast.error("La nueva contraseña debe ser distinta de la actual");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Contraseña actualizada");
      close();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 422) {
        toast.error(
          "La nueva contraseña no cumple el formato (mínimo 8 caracteres)",
        );
      } else {
        toast.error(detail || "No se pudo cambiar la contraseña");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4"
        onClick={close}
        data-testid="change-password-modal"
      >
        <motion.form
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          onClick={(e) => e.stopPropagation()}
          onSubmit={onSubmit}
          className="w-full max-w-md bg-med-surface border border-med-border rounded-2xl p-6 md:p-7"
          data-testid="change-password-form"
        >
          <header className="flex items-start justify-between gap-4 mb-5">
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-xl bg-med-primary/10 text-med-primary grid place-items-center shrink-0">
                <Key size={20} weight="duotone" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-med-text">
                  Cambiar contraseña
                </h2>
                <p className="text-sm text-med-muted">
                  Usa al menos 8 caracteres. Te seguirá funcionando la sesión
                  actual.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={close}
              className="p-1.5 -m-1.5 rounded-lg hover:bg-med-surface2 text-med-muted"
              data-testid="change-password-close-btn"
              aria-label="Cerrar"
            >
              <X size={18} weight="bold" />
            </button>
          </header>

          <Field
            label="Contraseña actual"
            value={form.current_password}
            onChange={onChange("current_password")}
            testid="current-password-input"
            autoComplete="current-password"
            required
          />
          <Field
            label="Nueva contraseña"
            value={form.new_password}
            onChange={onChange("new_password")}
            testid="new-password-input"
            autoComplete="new-password"
            required
            minLength={8}
          />
          <Field
            label="Confirmar nueva contraseña"
            value={form.confirm_password}
            onChange={onChange("confirm_password")}
            testid="confirm-password-input"
            autoComplete="new-password"
            required
            minLength={8}
          />

          <div className="flex justify-end gap-2 mt-6 pt-5 border-t border-med-border">
            <button
              type="button"
              onClick={close}
              className="px-4 py-2.5 rounded-xl border border-med-border hover:bg-med-surface2 text-med-text text-sm font-medium"
              data-testid="change-password-cancel-btn"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="change-password-submit-btn"
              className="inline-flex items-center gap-2 bg-med-primary hover:bg-med-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-5 py-2.5 font-semibold active:scale-[0.98] transition-colors"
            >
              {saving ? "Guardando…" : "Actualizar contraseña"}
            </button>
          </div>
        </motion.form>
      </motion.div>
    </AnimatePresence>
  );
}

function Field({
  label,
  value,
  onChange,
  testid,
  autoComplete,
  required,
  minLength,
}) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
        {label}
      </label>
      <input
        type="password"
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        autoComplete={autoComplete}
        data-testid={testid}
        className="w-full rounded-xl bg-white border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none transition-colors"
      />
    </div>
  );
}
